/** Rate cards and packaging catalogues: dated, versioned, added from CSV or Excel, never edited in place. */
import { catalogueFromRows, csvTemplate, rateCardFromRows } from "@ryoma/engine";
import type { Db } from "../data/db.ts";
import { loadTable } from "../lib/files.ts";
import { action, dropZone, date, gbp, h, mount, notice, num, table } from "./dom.ts";

export function downloadLink(label: string, fileName: string, content: string, type = "text/csv"): HTMLElement {
  const a = h("a", { href: "#", class: "small" }, label);
  a.addEventListener("click", (e) => {
    e.preventDefault();
    const url = URL.createObjectURL(new Blob([content], { type }));
    const tmp = h("a", { href: url, download: fileName });
    document.body.append(tmp);
    tmp.click();
    tmp.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
  return a;
}

function problemsNotice(problems: string[]): HTMLElement {
  return notice("error", h("strong", null, "Not saved. Fix these and try again:"), h("ul", null, problems.slice(0, 20).map((p) => h("li", null, p))));
}

const today = () => new Date().toISOString().slice(0, 10);

export async function referenceView(db: Db): Promise<HTMLElement> {
  const root = h("section", { class: "page" });

  const render = async () => {
    const [cards, cats] = await Promise.all([db.listRateCards(), db.listCatalogues()]);

    // Rate card form
    const rcId = h("input", { type: "text", placeholder: "e.g. royal-mail-business", "aria-label": "Rate card ID" });
    const rcVersion = h("input", { type: "text", placeholder: "e.g. 2026-10", "aria-label": "Version" });
    const rcFrom = h("input", { type: "date", value: today(), "aria-label": "Effective from" });
    const rcSource = h("input", { type: "text", placeholder: "Where the prices came from: published tariff, client invoice…", "aria-label": "Source" });
    const rcFile = h("input", { type: "file", accept: ".csv,.xlsx", "aria-label": "Rate card file" });
    const rcOut = h("div");
    const rcSave = action("Add rate card", async () => {
      const f = rcFile.files?.[0];
      if (!f) throw new Error("Choose a CSV or Excel file");
      const t = await loadTable(f);
      const { value, problems } = rateCardFromRows(t.rows, { id: rcId.value.trim(), version: rcVersion.value.trim(), effectiveFrom: rcFrom.value, source: rcSource.value });
      if (!value) {
        mount(rcOut, problemsNotice(problems));
        return;
      }
      await db.addRateCard(value);
      await render();
    }, { primary: true });

    // Catalogue form
    const pcId = h("input", { type: "text", placeholder: "e.g. ryoma-standard-formats", "aria-label": "Catalogue ID" });
    const pcVersion = h("input", { type: "text", placeholder: "e.g. 2026-10", "aria-label": "Catalogue version" });
    const pcFile = h("input", { type: "file", accept: ".csv,.xlsx", "aria-label": "Catalogue file" });
    const pcOut = h("div");
    const pcSave = action("Add catalogue", async () => {
      const f = pcFile.files?.[0];
      if (!f) throw new Error("Choose a CSV or Excel file");
      const t = await loadTable(f);
      const { value, problems } = catalogueFromRows(t.rows, { id: pcId.value.trim(), version: pcVersion.value.trim() });
      if (!value) {
        mount(pcOut, problemsNotice(problems));
        return;
      }
      await db.addCatalogue(value);
      await render();
    }, { primary: true });

    const cardList = cards.length === 0
      ? notice("info", "No rate cards yet.")
      : h("div", null, cards.map((c) =>
          h("details", { class: "card" },
            h("summary", null, `${c.id}@${c.version}`, h("span", { class: "muted small" }, ` · from ${date(c.effective_from)} · ${c.body.services.length} services · ${c.source}`)),
            c.body.source.startsWith("SAMPLE") ? notice("warn", "Sample prices, not a real tariff. Do not use for client work.") : "",
            c.body.services.map((s) =>
              table(["Band", "Longest × middle × shortest, cm", "Max kg", "Price"],
                s.bands.map((b) => [b.name, `${b.maxLengthCm} × ${b.maxWidthCm} × ${b.maxHeightCm}`, num(b.maxWeightKg, 2), gbp(b.pricePence)]),
                { numeric: [2, 3], caption: `${s.name} (${s.code})${s.volumetricDivisor ? ` · volumetric ÷${s.volumetricDivisor}` : ""}` })),
          )));

    const catList = cats.length === 0
      ? notice("info", "No packaging catalogues yet.")
      : h("div", null, cats.map((c) =>
          h("details", { class: "card" },
            h("summary", null, `${c.id}@${c.version}`, h("span", { class: "muted small" }, ` · ${c.body.formats.length} formats`)),
            table(["Format", "Kind", "Inside, cm", "Outside, cm", "Weight kg", "Unit cost"],
              c.body.formats.map((f) => [`${f.name} (${f.code})`, f.kind,
                `${f.innerDims.lengthCm} × ${f.innerDims.widthCm} × ${f.innerDims.heightCm}`,
                `${f.outerDims.lengthCm} × ${f.outerDims.widthCm} × ${f.outerDims.heightCm}`,
                num(f.tareWeightKg, 3), gbp(f.unitCostPence)]), { numeric: [4, 5] }),
          )));

    mount(root,
      h("h1", null, "Rate cards & packaging"),
      h("p", { class: "muted" }, "Prices are never edited: a change is a new version, so every report can say exactly which prices it used."),
      h("h2", null, "Rate cards"),
      cardList,
      h("details", { class: "card" },
        h("summary", null, "Add a rate card"),
        h("p", { class: "small" }, "One row per price band. ", downloadLink("Download the template", "rate-card-template.csv", csvTemplate("rate-card")),
          ". Leave volumetric_divisor blank for services that charge on actual weight only."),
        h("div", { class: "form-grid" },
          h("label", null, "ID"), rcId, h("label", null, "Version"), rcVersion, h("label", null, "Effective from"), rcFrom,
          h("label", null, "Source"), rcSource, h("span", null, "File"), dropZone(rcFile), h("span"), rcSave),
        rcOut,
      ),
      h("h2", null, "Packaging catalogues"),
      catList,
      h("details", { class: "card" },
        h("summary", null, "Add a packaging catalogue"),
        h("p", { class: "small" }, "One row per box, mailer or envelope. ", downloadLink("Download the template", "packaging-template.csv", csvTemplate("catalogue")), "."),
        h("div", { class: "form-grid" },
          h("label", null, "ID"), pcId, h("label", null, "Version"), pcVersion, h("span", null, "File"), dropZone(pcFile), h("span"), pcSave),
        pcOut,
      ),
    );
  };

  await render();
  return root;
}
